import React, { useMemo, useState } from "react";
import Edit from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import CardMedia from "@mui/material/CardMedia";
import { FullScreenDialog } from "../core/FullScreenDialog";
import { SmartTextField } from "./SmartTextField";
import { SmartDateField } from "../core/SmartDateField";
import { PublisherSelect } from "./royalties/PublisherSelect";
import {
  useDeleteGameEdition,
  useMyGameEditions,
  useUpdateGameEdition,
} from "@seabrookstudios/pitch2table";
import { GameEditionCoverUpload } from "./GameEditionCoverUpload";

/**
 * Custom edition covers are stored under games/{gameId}/editions/{editionId}/cover.
 * Injected game-image fallbacks use a different path and should not be previewed here.
 * @param {import("@seabrookstudios/types").Url|string|undefined} image
 * @param {import("@seabrookstudios/pitch2table-core").GameEditionId} editionId
 */
const isCustomEditionCover = (image, editionId) => {
  if (!image) {
    return false;
  }
  return image.includes(`/editions/${editionId}/cover`);
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").GameEdition} props.edition
 * @param {import("react").ReactNode} [props.CustomControl]
 */
export default function EditGameEditionFullScreenDialog({ gameId, edition, CustomControl = undefined }) {
  const [name, setName] = useState(edition.name);
  const [datePublished, setDatePublished] = useState(edition.datePublished);
  const [publisherId, setPublisherId] = useState(edition.publisherId);

  const { data: editions = [] } = useMyGameEditions(gameId);
  const liveEdition = useMemo(
    () => editions.find((item) => item.id === edition.id) ?? edition,
    [editions, edition]
  );
  const coverImage = isCustomEditionCover(liveEdition.image, edition.id)
    ? liveEdition.image
    : undefined;

  const updateEdition = useUpdateGameEdition(gameId, edition.id);
  const deleteEdition = useDeleteGameEdition(gameId, edition.id);

  const onSaveHandler = async () => {
    await updateEdition.mutateAsync({
      name: name.trim(),
      datePublished,
      publisherId: publisherId === "unset" ? undefined : publisherId,
    });
  };

  const onDeleteHandler = async () => {
    await deleteEdition.mutateAsync();
  };

  return (
    <FullScreenDialog
      title="Edit Edition"
      actionText="save"
      onSaveHandler={onSaveHandler}
      onDeleteHandler={onDeleteHandler}
      canSave={name.trim().length === 0}
      isBusy={updateEdition.isPending || deleteEdition.isPending}
      CustomControl={
        CustomControl || (
          <IconButton color="primary" size="small">
            <Edit />
          </IconButton>
        )
      }
    >
      <Stack spacing={2}>
        {coverImage ? (
          <CardMedia
            component="img"
            sx={{ maxHeight: 200, objectFit: "contain" }}
            image={coverImage}
            title={`Edition cover for ${liveEdition.name}`}
          />
        ) : null}
        <SmartTextField
          required
          label="Edition Name"
          helperText="What is this release called?"
          onChange={setName}
          initialValue={edition.name}
        />
        <SmartDateField
          label="Date Published"
          helperText="When was this edition released?"
          initialValue={edition.datePublished}
          onChange={setDatePublished}
        />
        <PublisherSelect
          gameId={gameId}
          value={publisherId ?? "unset"}
          onChange={(value) => setPublisherId(value === "unset" ? undefined : value)}
          helperText="Which publisher released this edition?"
        />
        <GameEditionCoverUpload gameId={gameId} editionId={edition.id} />
      </Stack>
    </FullScreenDialog>
  );
}
